import React, { useMemo, useRef, useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { apiService } from '../../services/api';
import { getAllDeliverySlotLabels } from '../../utils/deliverySlots';
import { formatTimeSaudi } from '../../utils/saudiTime';

export default function SalesOrders() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [paymentUpdatingId, setPaymentUpdatingId] = useState(null);
  const [deliveredUpdatingId, setDeliveredUpdatingId] = useState(null);
  const [editingOrder, setEditingOrder] = useState(null);
  const [editFormData, setEditFormData] = useState({
    customerName: '',
    customerPhone: '',
    deliverySlot: '',
    items: []
  });
  const [updatingOrder, setUpdatingOrder] = useState(null);
  const [slotCounts, setSlotCounts] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState('');
  const ordersSectionRef = useRef(null);
  const [slotModalSlot, setSlotModalSlot] = useState('');
  const [isSlotModalOpen, setIsSlotModalOpen] = useState(false);

  const canDelete = user?.role === 'admin' || user?.role === 'master_admin';
  const canMarkPayment = user?.role === 'custom_role_sales' || user?.role === 'admin' || user?.role === 'master_admin';
  const canEdit = user?.role === 'custom_role_sales' || user?.role === 'admin' || user?.role === 'master_admin';
  const isSalesUser = user?.role === 'custom_role_sales';
  const pendingSalesStatuses = ['Pending', 'Sent to Central Kitchen'];

  const loadOrders = async () => {
    setError('');
    setLoading(true);
    try {
      const [ordersRes, slotCountsRes] = await Promise.all([
        apiService.orders.getSalesOrders(),
        apiService.orders.getSalesOrderSlotCounts(),
      ]);
      if (Array.isArray(ordersRes.data)) setOrders(ordersRes.data);
      setSlotCounts(slotCountsRes.data?.countsBySlot || {});
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrders();
  }, []);

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    setDeletingId(deleteConfirm._id);
    setError('');
    try {
      await apiService.orders.delete(deleteConfirm._id);
      setDeleteConfirm(null);
      await loadOrders();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to delete order');
    } finally {
      setDeletingId(null);
    }
  };

  const handleMarkPaymentReceived = async (orderId) => {
    if (isSalesUser) {
      const ok = window.confirm('Confirm payment received?\n\nThis action cannot be undone.');
      if (!ok) return;
    }
    setPaymentUpdatingId(orderId);
    setError('');
    try {
      await apiService.orders.markSalesOrderPaymentReceived(orderId);
      await loadOrders();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to mark payment received');
    } finally {
      setPaymentUpdatingId(null);
    }
  };

  const handleMarkDelivered = async (orderId) => {
    if (!orderId) return;
    if (isSalesUser) {
      const ok = window.confirm('Mark this order as delivered?\n\nThis action cannot be undone.');
      if (!ok) return;
    }
    setError('');
    setDeliveredUpdatingId(orderId);
    try {
      await apiService.orders.markSalesOrderDelivered(orderId);
      const res = await apiService.orders.getSalesOrders();
      setOrders(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to mark delivered');
    } finally {
      setDeliveredUpdatingId(null);
    }
  };

  const formatDateTime = formatTimeSaudi;

  const handleEditClick = (order) => {
    if (isSalesUser && !pendingSalesStatuses.includes(order.status)) {
      setError('Order already accepted by order receiver. This order cannot be edited.');
      return;
    }
    if (order.paymentReceived) {
      setError('Payment has been received. This order is locked and cannot be edited.');
      return;
    }
    setEditingOrder(order);
    setEditFormData({
      customerName: order.customerName || '',
      customerPhone: order.customerPhone || '',
      deliverySlot: order.deliverySlot || '',
      items: order.items || []
    });
    setError('');
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editingOrder) return;
    
    setUpdatingOrder(editingOrder._id);
    setError('');
    
    try {
      // Calculate total amount from items
      let totalAmount = 0;
      const updatedItems = editFormData.items.map((item) => {
        const qty = Number(item.orderQty) || 0;
        const unitPrice = Number(item.unitPrice) || 0;
        totalAmount += qty * unitPrice;
        return item;
      });

      await apiService.orders.update(editingOrder._id, {
        customerName: editFormData.customerName.trim() || undefined,
        customerPhone: editFormData.customerPhone.trim() || undefined,
        deliverySlot: editFormData.deliverySlot || undefined,
        items: updatedItems,
        totalAmount: totalAmount > 0 ? totalAmount : undefined
      });
      
      setEditingOrder(null);
      await loadOrders();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to update order');
    } finally {
      setUpdatingOrder(null);
    }
  };

  const handleItemQuantityChange = (itemIndex, newQty) => {
    const qty = Math.max(0, Number(newQty) || 0);
    setEditFormData(prev => ({
      ...prev,
      items: prev.items.map((item, idx) => 
        idx === itemIndex ? { ...item, orderQty: qty } : item
      )
    }));
  };

  const slotSummary = useMemo(() => {
    const counts = slotCounts || {};
    const noSlotCount = Number(counts[''] || 0);
    const totalOrders = Object.values(counts).reduce((sum, v) => sum + (Number(v) || 0), 0);
    const orderedSlots = Object.entries(counts)
      .filter(([k, v]) => k && Number(v) > 0)
      .map(([label, count]) => {
        const slotOrders = orders.filter((o) => (o.deliverySlot || '') === label);
        const allPaid =
          slotOrders.length > 0 && slotOrders.every((o) => Boolean(o.paymentReceived));
        return { label, count: Number(count), allPaid };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
    return { orderedSlots, noSlotCount, totalOrders };
  }, [slotCounts, orders]);

  const filteredOrders = selectedSlot
    ? orders.filter((o) => (o.deliverySlot || '') === selectedSlot)
    : orders;

  const openSlotModal = (slotLabel) => {
    setSlotModalSlot(slotLabel);
    setIsSlotModalOpen(true);
  };

  const closeSlotModal = () => {
    setIsSlotModalOpen(false);
    setSlotModalSlot('');
  };

  const showFullDetailsForSlot = (slotLabel) => {
    setSelectedSlot(slotLabel);
    closeSlotModal();
    setTimeout(() => {
      ordersSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  const modalOrders = useMemo(() => {
    if (!slotModalSlot) return [];
    return orders.filter((o) => (o.deliverySlot || '') === slotModalSlot);
  }, [orders, slotModalSlot]);

  return (
    <div className="p-3 sm:p-4 md:p-6">
      <div className="bg-white rounded-lg shadow-md border-l-4 border-blue-500 p-3 sm:p-4 md:p-6">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-700 mb-2 sm:mb-4">Orders</h1>
        <p className="text-gray-600 text-sm mb-4 sm:mb-6">All sales orders — pending, confirmed, and rejected.</p>

        {!loading && slotCounts && (
          <div className="mb-5">
            <div className="text-sm font-semibold text-gray-700 mb-2">Slot wise number of orders</div>
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => setSelectedSlot('')}
                className={`w-full flex items-center justify-between border rounded-lg px-4 py-3 text-left ${
                  !selectedSlot
                    ? 'border-blue-300 bg-blue-50'
                    : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                <span className="font-medium text-gray-800">All Orders</span>
                <span className="font-bold text-gray-800">{slotSummary.totalOrders} Orders</span>
              </button>
              {slotSummary.orderedSlots.map((s) => (
                <button
                  key={s.label}
                  type="button"
                  onClick={() => openSlotModal(s.label)}
                  className={`w-full flex items-center justify-between border rounded-lg px-4 py-3 text-left ${
                    s.allPaid
                      ? 'border-emerald-300 bg-emerald-50'
                      : selectedSlot === s.label
                      ? 'border-blue-300 bg-blue-50'
                      : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <span className="font-medium text-gray-800">{s.label}</span>
                  <span className={`font-bold ${s.allPaid ? 'text-emerald-700' : 'text-red-600'}`}>
                    {s.count} Orders
                  </span>
                </button>
              ))}
              {slotSummary.noSlotCount > 0 && (
                <button
                  type="button"
                  onClick={() => setSelectedSlot('')}
                  className="w-full flex items-center justify-between border border-gray-200 rounded-lg px-4 py-3 bg-gray-50"
                  title="Orders without slot are shown under All Orders"
                >
                  <span className="font-medium text-gray-700">No slot selected</span>
                  <span className="font-bold text-gray-700">{slotSummary.noSlotCount} Orders</span>
                </button>
              )}
            </div>
          </div>
        )}

        {isSlotModalOpen && (
          <div
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/50"
            onClick={closeSlotModal}
          >
            <div
              className="bg-white rounded-t-2xl sm:rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-4 sm:p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between gap-3 mb-4">
                <div className="min-w-0">
                  <div className="text-lg sm:text-xl font-bold text-gray-800 truncate">{slotModalSlot}</div>
                  <div className="text-sm text-gray-600 mt-0.5">
                    {modalOrders.length} order(s)
                  </div>
                </div>
                <button
                  type="button"
                  onClick={closeSlotModal}
                  className="text-gray-500 hover:text-gray-700 p-2 rounded-lg hover:bg-gray-100"
                  aria-label="Close"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-between mb-4">
                <button
                  type="button"
                  onClick={() => showFullDetailsForSlot(slotModalSlot)}
                  className="bg-blue-600 hover:bg-blue-700 text-white py-2.5 px-4 rounded-lg font-semibold"
                >
                  Show full details
                </button>
                <div className="text-xs text-gray-500">
                  Compact view: tap “Show full details” to see full cards.
                </div>
              </div>

              {modalOrders.length === 0 ? (
                <div className="py-8 text-center text-gray-500 border border-gray-200 rounded-lg">
                  No orders in this slot.
                </div>
              ) : (
                <div className="space-y-2">
                  {modalOrders.map((o) => {
                    const itemsCount = (o.items || []).reduce((sum, i) => sum + (i.orderQty || 0), 0);
                    return (
                      <div key={o._id} className="border border-gray-200 rounded-lg px-4 py-3">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="font-semibold text-gray-800 truncate">{o.orderNo}</div>
                            <div className="text-sm text-gray-600 mt-0.5">
                              {formatDateTime(o.dateTime)} · {itemsCount} item(s)
                              {o.customerName ? ` · ${o.customerName}` : ''}
                            </div>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <div className="text-xs font-semibold text-gray-700">{o.status || '—'}</div>
                            {o.totalAmount != null && Number(o.totalAmount) > 0 && (
                              <div className="text-sm font-bold text-gray-900 tabular-nums">
                                SAR {Number(o.totalAmount).toFixed(2)}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}
        {loading && (
          <div className="py-8 text-center text-gray-500">Loading orders…</div>
        )}
        <div ref={ordersSectionRef} />

        {!loading && selectedSlot && (
          <div className="mb-4 p-3 border border-blue-200 bg-blue-50 rounded-lg text-sm text-gray-800">
            Showing <span className="font-bold">{filteredOrders.length}</span> order(s) in{' '}
            <span className="font-bold">{selectedSlot}</span>.
          </div>
        )}

        {!loading && filteredOrders.length === 0 && (
          <div className="bg-white rounded-lg p-8 text-center text-gray-600 border border-gray-200 border-l-4 border-l-green-400">
            <p className="mb-2">No orders found.</p>
            <p className="text-sm">Orders submitted from Create order will appear here.</p>
          </div>
        )}
        {!loading && filteredOrders.length > 0 && (
          <div className="space-y-4">
            {filteredOrders.map((order) => {
              const itemCount = (order.items || []).reduce((sum, i) => sum + (i.orderQty || 0), 0);
              const isPending = order.status === 'Pending' || order.status === 'Sent to Central Kitchen';
              const sideLineClass =
                order.status === 'Rejected'
                  ? 'border-l-gray-900'
                  : !order.delivered && isPending
                  ? 'border-l-orange-200'
                  : order.delivered && !order.paymentReceived
                  ? 'border-l-red-500'
                  : order.paymentReceived
                  ? 'border-l-emerald-500'
                  : 'border-l-blue-400';
              return (
                <div
                  key={order._id}
                  className={`border border-gray-200 rounded-lg p-3 sm:p-4 border-l-8 ${sideLineClass} hover:shadow-md transition-shadow`}
                >
                  <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-gray-800 text-base sm:text-lg">{order.orderNo}</div>
                      <div className="text-base text-gray-500 mt-1">
                        {formatDateTime(order.dateTime)} · {itemCount} item(s)
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
                      <span
                        className={`inline-block px-3 py-1.5 rounded-full text-sm font-medium border ${
                          order.status === 'Received' && order.paymentReceived
                            ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
                            : order.status === 'Received' && order.delivered && !order.paymentReceived
                            ? 'bg-amber-100 text-amber-800 border-amber-200'
                            : order.status === 'Received' && !order.delivered
                            ? 'bg-amber-100 text-amber-800 border-amber-200'
                            : order.status === 'Rejected'
                            ? 'bg-red-100 text-red-800 border-red-200'
                            : 'bg-orange-100 text-orange-800 border-orange-200'
                        }`}
                      >
                        {order.status === 'Sent to Central Kitchen'
                          ? 'Pending'
                          : order.status === 'Received' && order.paymentReceived
                          ? 'Payment received'
                          : order.status === 'Received' && order.delivered && !order.paymentReceived
                          ? 'Pending payment'
                          : order.status === 'Received' && !order.delivered
                          ? 'Confirmed'
                          : (order.status || '—')}
                      </span>
                      {order.status === 'Received' && order.delivered && !order.paymentReceived && (
                        <span
                          title="Payment pending"
                          className="inline-block h-3 w-3 rounded-full bg-red-500 border border-red-200"
                        />
                      )}
                      {order.delivered && (
                        <span className="inline-block px-3 py-1.5 rounded-full text-sm font-bold bg-blue-100 text-blue-800 border border-blue-200">
                          ✓ Delivered
                        </span>
                      )}
                      {canEdit &&
                        !order.paymentReceived &&
                        (!isSalesUser || pendingSalesStatuses.includes(order.status)) && (
                        <button
                          type="button"
                          onClick={() => handleEditClick(order)}
                          disabled={updatingOrder === order._id}
                          className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium py-2 px-4 rounded-lg min-h-[40px] touch-manipulation"
                        >
                          Edit
                        </button>
                      )}
                      {canEdit && order.status === 'Received' && !order.delivered && (
                        <button
                          type="button"
                          onClick={() => handleMarkDelivered(order._id)}
                          disabled={deliveredUpdatingId != null}
                          className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium py-2 px-4 rounded-lg min-h-[40px] touch-manipulation"
                        >
                          {deliveredUpdatingId === order._id
                            ? 'Updating…'
                            : 'Mark delivered'}
                        </button>
                      )}
                      {canMarkPayment && order.status === 'Received' && order.delivered && !order.paymentReceived && (
                        <button
                          type="button"
                          onClick={() => handleMarkPaymentReceived(order._id)}
                          disabled={paymentUpdatingId != null}
                          className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm font-medium py-2 px-4 rounded-lg min-h-[40px] touch-manipulation"
                        >
                          {paymentUpdatingId === order._id ? 'Updating…' : 'Confirm payment received'}
                        </button>
                      )}
                      {canDelete && (
                        <button
                          type="button"
                          onClick={() => setDeleteConfirm(order)}
                          disabled={deletingId != null}
                          className="text-red-600 hover:text-red-800 text-sm font-medium border border-red-200 hover:bg-red-50 px-3 py-2 rounded min-h-[40px] touch-manipulation disabled:opacity-50"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                  {(order.customerNo || order.customerId || order.customerName || order.customerPhone || order.deliverySlot) && (
                    <div className="text-base sm:text-lg text-gray-700 mt-2 font-medium">
                      {(order.customerNo || order.customerId) && (
                        <span className="block sm:inline">
                          Customer ID:{' '}
                          <span className="text-gray-900 font-mono tabular-nums">
                            {order.customerNo || String(order.customerId)}
                          </span>
                        </span>
                      )}
                      {order.customerName && (
                        <span className="block sm:inline mt-0.5 sm:mt-0 sm:ml-4">
                          Customer:{' '}
                          <span className="text-gray-900">{order.customerName}</span>
                        </span>
                      )}
                      {order.customerPhone && (
                        <span className="block sm:inline mt-0.5 sm:mt-0 sm:ml-4">
                          Phone: <span className="text-gray-900">{order.customerPhone}</span>
                        </span>
                      )}
                      {order.deliverySlot && (
                        <div className="block sm:inline mt-2 sm:mt-0 sm:ml-4">
                          <span className="text-base sm:text-lg font-semibold text-gray-800">Delivery Slot: </span>
                          <span className="text-base sm:text-lg font-bold text-blue-700">{order.deliverySlot}</span>
                        </div>
                      )}
                    </div>
                  )}
                  {order.items && order.items.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <ul className="space-y-2 text-base text-gray-700">
                        {order.items.map((item, idx) => {
                          const qty = Number(item.orderQty) || 0;
                          const unitPrice = Number(item.unitPrice) || 0;
                          const lineTotal = qty * unitPrice;
                          return (
                            <li key={idx} className="flex justify-between gap-3">
                              <span>
                                {item.itemName} × {qty}
                              </span>
                              <span className="font-medium tabular-nums whitespace-nowrap">
                                {unitPrice > 0 ? `SAR ${lineTotal.toFixed(2)}` : '—'}
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                      {order.totalAmount != null && Number(order.totalAmount) > 0 && (
                        <div className="flex justify-between text-base font-semibold text-gray-800 mt-3 pt-2 border-t border-gray-100">
                          <span>Total</span>
                          <span className="tabular-nums">SAR {Number(order.totalAmount).toFixed(2)}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {editingOrder && (
          <div
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/50"
            onClick={() => !updatingOrder && setEditingOrder(null)}
          >
            <div
              className="bg-white rounded-t-2xl sm:rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto p-4 sm:p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-xl font-bold text-gray-800 mb-4">Edit Order</h2>
              {editingOrder.isReady && (
                <div className="mb-4 p-3 bg-yellow-100 border border-yellow-300 text-yellow-800 rounded-lg text-sm font-medium">
                  ⚠️ Order is already ready and cannot be edited.
                </div>
              )}
              <form onSubmit={handleEditSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Customer name *</label>
                  <input
                    type="text"
                    value={editFormData.customerName}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, customerName: e.target.value }))}
                    placeholder="Customer name"
                    required
                    disabled={editingOrder.isReady || updatingOrder === editingOrder._id}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-gray-700 focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone number</label>
                  <input
                    type="text"
                    value={editFormData.customerPhone}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, customerPhone: e.target.value }))}
                    placeholder="Phone number"
                    disabled={editingOrder.isReady || updatingOrder === editingOrder._id}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-gray-700 focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Delivery Slot</label>
                  <select
                    value={editFormData.deliverySlot}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, deliverySlot: e.target.value }))}
                    disabled={editingOrder.isReady || updatingOrder === editingOrder._id}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-gray-700 focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    <option value="">Select delivery slot</option>
                    {getAllDeliverySlotLabels().map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Items</label>
                  <div className="space-y-2 max-h-60 overflow-y-auto bg-gray-50 rounded-lg p-3">
                    {editFormData.items.map((item, idx) => {
                      const qty = Number(item.orderQty) || 0;
                      const unitPrice = Number(item.unitPrice) || 0;
                      return (
                        <div key={idx} className="flex items-center justify-between gap-3 p-2 bg-white rounded border border-gray-200">
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-gray-800 text-sm">{item.itemName}</div>
                            <div className="text-xs text-gray-500">SAR {unitPrice.toFixed(2)} each</div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => handleItemQuantityChange(idx, qty - 1)}
                              disabled={editingOrder.isReady || updatingOrder === editingOrder._id || qty <= 0}
                              className="min-w-[32px] h-8 rounded bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              −
                            </button>
                            <input
                              type="number"
                              min="0"
                              value={qty}
                              onChange={(e) => handleItemQuantityChange(idx, e.target.value)}
                              disabled={editingOrder.isReady || updatingOrder === editingOrder._id}
                              className="w-16 text-center border border-gray-300 rounded px-2 py-1 text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                            />
                            <button
                              type="button"
                              onClick={() => handleItemQuantityChange(idx, qty + 1)}
                              disabled={editingOrder.isReady || updatingOrder === editingOrder._id}
                              className="min-w-[32px] h-8 rounded bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              +
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-2 text-sm font-semibold text-gray-800">
                    Total: SAR {editFormData.items.reduce((sum, item) => sum + (Number(item.orderQty) || 0) * (Number(item.unitPrice) || 0), 0).toFixed(2)}
                  </div>
                </div>
                {error && (
                  <div className="p-2 bg-red-100 text-red-700 text-sm rounded">{error}</div>
                )}
                <div className="flex gap-3 pt-2">
                  <button
                    type="submit"
                    disabled={editingOrder.isReady || updatingOrder === editingOrder._id}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white py-2 px-4 rounded-lg font-medium"
                  >
                    {updatingOrder === editingOrder._id ? 'Updating…' : 'Update Order'}
                  </button>
                  <button
                    type="button"
                    onClick={() => !updatingOrder && setEditingOrder(null)}
                    disabled={updatingOrder === editingOrder._id}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {deleteConfirm && (
          <div
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/50"
            onClick={() => !deletingId && setDeleteConfirm(null)}
          >
            <div
              className="bg-white rounded-t-2xl sm:rounded-lg shadow-xl w-full max-w-sm p-4 sm:p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-lg font-bold text-gray-800 mb-2">Delete order?</h2>
              <p className="text-gray-600 text-sm mb-4">
                &quot;{deleteConfirm.orderNo}&quot; will be removed. This cannot be undone.
              </p>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={deletingId != null}
                  className="bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white py-2 px-4 rounded-lg font-medium"
                >
                  {deletingId ? 'Deleting…' : 'Delete'}
                </button>
                <button
                  type="button"
                  onClick={() => !deletingId && setDeleteConfirm(null)}
                  disabled={deletingId != null}
                  className="py-2 px-4 border rounded-lg text-gray-700"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
